<template>
  <div class="effect-report-editor-page" v-loading="loading">
    <header class="editor-toolbar">
      <div class="editor-toolbar__identity">
        <el-button
          size="small"
          icon="el-icon-arrow-left"
          @click="returnToReport"
        >
          返回月报
        </el-button>
        <div>
          <h1>月报编辑</h1>
          <p>{{ editorSubtitle }}</p>
        </div>
      </div>
      <div class="editor-toolbar__filters">
        <el-select
          v-model="siteId"
          class="editor-toolbar__site"
          size="small"
          filterable
          clearable
          placeholder="选择站点"
          @change="onSiteChange"
        >
          <el-option
            v-for="site in siteOptions"
            :key="site.id"
            :label="`${site.name}（${site.url}）`"
            :value="site.id"
          />
        </el-select>
        <el-select
          v-model="statMonth"
          class="editor-toolbar__month"
          size="small"
          clearable
          :disabled="!siteId"
          placeholder="选择月份"
          @change="onMonthChange"
        >
          <el-option
            v-for="month in monthOptions"
            :key="month.stat_month"
            :label="month.label"
            :value="month.stat_month"
          >
            <span>{{ month.label }}</span>
            <span class="month-option__status">
              {{ monthStatusText(month.status) }}
            </span>
          </el-option>
        </el-select>
        <el-tag v-if="reportState.id" :type="stateTagType" size="small">
          {{ reportStateText }}
        </el-tag>
      </div>
    </header>

    <div v-if="!siteId || !statMonth" class="editor-selection-empty">
      <i class="el-icon-edit-outline"></i>
      <strong>请选择需要编辑的站点和月份</strong>
    </div>

    <el-form
      v-else-if="editor"
      class="editor-surface"
      label-position="top"
      size="small"
    >
      <section class="form-section">
        <div class="form-section__head">
          <div>
            <h2>运营数据</h2>
          </div>
        </div>
        <div class="operation-editor">
          <el-form-item
            v-for="item in operationForm"
            :key="item.metric_type"
            :label="item.label"
          >
            <el-input-number
              v-model="item.monthly_count"
              :min="0"
              :max="999999"
              controls-position="right"
            />
          </el-form-item>
        </div>
      </section>

      <section class="form-section">
        <div class="form-section__head">
          <div>
            <h2>优化记录</h2>
          </div>
        </div>
        <div class="notes-editor">
          <el-form-item label="本月优化记录">
            <el-input
              v-model="notesForm.current_summary"
              type="textarea"
              :rows="8"
              maxlength="20000"
              show-word-limit
            />
          </el-form-item>
          <el-form-item label="下月优化计划">
            <el-input
              v-model="notesForm.next_plan"
              type="textarea"
              :rows="8"
              maxlength="20000"
              show-word-limit
            />
          </el-form-item>
        </div>
      </section>

      <section class="form-section">
        <div class="form-section__head">
          <div>
            <h2>Technical SEO</h2>
            <p>
              已完成 {{ completedTechnicalKeys.length }} /
              {{ technicalItems.length }}
            </p>
          </div>
          <el-checkbox
            class="technical-select-all"
            :value="technicalAllSelected"
            :indeterminate="technicalIndeterminate"
            @change="toggleTechnicalAll"
          >
            全选
          </el-checkbox>
        </div>
        <el-checkbox-group
          v-model="completedTechnicalKeys"
          class="technical-editor"
        >
          <el-checkbox
            v-for="item in technicalItems"
            :key="item.item_key"
            :label="item.item_key"
          >
            {{ item.item_name }}
          </el-checkbox>
        </el-checkbox-group>
      </section>

      <section class="form-section">
        <div class="form-section__head">
          <div>
            <h2>项目成员</h2>
            <p>已添加 {{ memberForm.length }} / 20</p>
          </div>
          <div class="member-editor__actions">
            <span>月报显示</span>
            <el-switch
              v-model="membersEnabled"
              active-text="开启"
              inactive-text="关闭"
            />
            <el-button
              size="mini"
              icon="el-icon-plus"
              :disabled="!membersEnabled || memberForm.length >= 20"
              @click="addMember"
            >
              添加成员
            </el-button>
          </div>
        </div>
        <div v-if="membersEnabled && memberForm.length" class="member-editor">
          <div class="member-editor__columns" aria-hidden="true">
            <span>角色</span>
            <span>姓名</span>
            <span>电话</span>
            <span>邮箱</span>
            <span>联系图片</span>
            <span>操作</span>
          </div>
          <div
            v-for="(member, index) in memberForm"
            :key="index"
            class="member-editor__row"
          >
            <el-input v-model="member.role" placeholder="角色" />
            <el-input v-model="member.name" placeholder="姓名" />
            <el-input v-model="member.phone" placeholder="电话" />
            <el-input v-model="member.email" placeholder="邮箱" />
            <div class="member-editor__image-field">
              <div class="member-editor__image-preview">
                <img
                  v-if="member.contact_image_path"
                  :src="member.contact_image_path"
                  :alt="`${member.name || '成员'}联系图片`"
                />
                <i v-else class="el-icon-picture-outline"></i>
              </div>
              <upload
                :multiple="false"
                :limit="1"
                accept="image/png,image/jpeg,image/webp"
                :data="{ scope: 'effect_report' }"
                @finish="onMemberImageUpload(index, $event)"
              >
                <el-tooltip content="上传联系图片" placement="top">
                  <el-button
                    size="mini"
                    icon="el-icon-upload2"
                    aria-label="上传联系图片"
                  />
                </el-tooltip>
              </upload>
              <el-tooltip
                v-if="member.contact_image_path"
                content="清除联系图片"
                placement="top"
              >
                <el-button
                  type="text"
                  icon="el-icon-close"
                  aria-label="清除联系图片"
                  @click="member.contact_image_path = ''"
                />
              </el-tooltip>
            </div>
            <el-tooltip content="删除成员" placement="top">
              <el-button
                type="text"
                icon="el-icon-delete"
                class="member-editor__delete"
                aria-label="删除成员"
                @click="removeMember(index)"
              />
            </el-tooltip>
          </div>
        </div>
        <div v-else-if="membersEnabled" class="editor-empty">暂无项目成员</div>
        <div v-else class="editor-empty">项目成员模块已关闭</div>
      </section>

      <footer class="editor-actions">
        <div>
          <span v-if="hasUnsavedChanges" class="editor-actions__dirty">
            有未保存修改
          </span>
          <span v-else>修订 {{ reportState.revision || 1 }}</span>
        </div>
        <el-button
          type="primary"
          size="small"
          icon="el-icon-check"
          :loading="saving"
          :disabled="!canSave"
          @click="saveDraft"
        >
          保存草稿
        </el-button>
      </footer>
    </el-form>
  </div>
</template>

<script>
import Upload from "@/components/upload/index.vue";
import {
  apiSeoEffectReportEditorDetail,
  apiSeoEffectReportEditorOptions,
  apiSeoEffectReportSave,
} from "@/api/seo";

export default {
  name: "SeoEffectReportEdit",
  components: { Upload },
  data() {
    return {
      loading: false,
      saving: false,
      siteId: "",
      statMonth: "",
      siteOptions: [],
      monthOptions: [],
      reportState: {},
      editor: null,
      operationForm: [],
      notesForm: {
        current_summary: "",
        next_plan: "",
      },
      technicalItems: [],
      completedTechnicalKeys: [],
      membersEnabled: true,
      memberForm: [],
      savedFingerprint: "",
      allowRouteLeave: false,
    };
  },
  computed: {
    canEdit() {
      return this.$hasPermission("seo.effect_report", "edit");
    },
    technicalAllSelected() {
      return (
        this.technicalItems.length > 0 &&
        this.completedTechnicalKeys.length === this.technicalItems.length
      );
    },
    technicalIndeterminate() {
      return (
        this.completedTechnicalKeys.length > 0 &&
        this.completedTechnicalKeys.length < this.technicalItems.length
      );
    },
    editorSubtitle() {
      const meta = (this.editor || {}).meta || {};
      if (meta.site_name || meta.site_url) {
        return `${meta.site_name || meta.site_url} · ${
          meta.month_label || this.statMonth
        }`;
      }
      return "选择站点和月份后维护月报内容";
    },
    reportStateText() {
      return this.reportState.status === "published" ? "已发布" : "草稿";
    },
    stateTagType() {
      return this.reportState.status === "published" ? "success" : "info";
    },
    formFingerprint() {
      if (!this.editor) return "";
      return JSON.stringify({
        operation_stats: this.operationForm.map((item) => ({
          metric_type: item.metric_type,
          monthly_count: item.monthly_count,
        })),
        current_summary: this.notesForm.current_summary,
        next_plan: this.notesForm.next_plan,
        completed_technical_keys: this.completedTechnicalKeys.slice().sort(),
        members_enabled: this.membersEnabled,
        members: this.memberForm.map((item) => ({
          role: item.role,
          name: item.name,
          phone: item.phone,
          email: item.email,
          contact_image_path: item.contact_image_path,
        })),
      });
    },
    hasUnsavedChanges() {
      return Boolean(
        this.editor &&
          this.savedFingerprint &&
          this.formFingerprint !== this.savedFingerprint
      );
    },
    canSave() {
      return (
        this.canEdit &&
        this.editor &&
        this.hasUnsavedChanges &&
        !this.loading &&
        !this.saving
      );
    },
  },
  created() {
    const query = this.$route.query || {};
    const siteId = Number(query.site_id || 0);
    const statMonth = String(query.stat_month || "");
    if (siteId > 0) {
      this.siteId = siteId;
    }
    if (/^\d{4}-(?:0[1-9]|1[0-2])$/.test(statMonth)) {
      this.statMonth = statMonth;
    }
    this.initialize();
  },
  mounted() {
    window.addEventListener("beforeunload", this.onBeforeUnload);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    if (this.allowRouteLeave || !this.hasUnsavedChanges) {
      next();
      return;
    }
    this.$confirm("当前月报有未保存修改，确定离开吗？", "未保存修改", {
      type: "warning",
    })
      .then(() => {
        this.allowRouteLeave = true;
        next();
      })
      .catch(() => next(false));
  },
  methods: {
    initialize() {
      return this.loadOptions().then(() => {
        if (this.siteId && this.statMonth) {
          return this.loadEditor();
        }
        return null;
      });
    },
    loadOptions() {
      this.loading = true;
      return apiSeoEffectReportEditorOptions({
        site_id: this.siteId || undefined,
      })
        .then((response) => {
          const data = response || {};
          this.siteOptions = data.site_options || [];
          this.monthOptions = data.month_options || [];
          if (
            this.statMonth &&
            !this.monthOptions.some(
              (item) => item.stat_month === this.statMonth
            )
          ) {
            this.statMonth = "";
            this.clearEditor();
            this.syncRoute();
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadEditor() {
      if (!this.siteId || !this.statMonth) {
        this.clearEditor();
        return Promise.resolve();
      }
      this.loading = true;
      return apiSeoEffectReportEditorDetail({
        site_id: this.siteId,
        stat_month: this.statMonth,
      })
        .then((response) => {
          this.applyEditorResponse(response);
          this.syncRoute();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    applyEditorResponse(response) {
      const data = response || {};
      this.siteOptions = data.site_options || this.siteOptions;
      this.monthOptions = data.month_options || this.monthOptions;
      this.reportState = data.report_state || {};
      this.fillForm(data.editor || null);
    },
    applySaveResponse(response) {
      const data = response || {};
      this.siteOptions = data.site_options || this.siteOptions;
      this.monthOptions = data.month_options || this.monthOptions;
      this.reportState = data.report_state || {};
      this.fillForm(data.report || null);
    },
    fillForm(source) {
      if (!source) {
        this.clearEditor();
        return;
      }
      const meta = source.meta || {};
      this.editor = { meta };
      this.siteId = Number(meta.site_id || this.siteId);
      this.statMonth = meta.stat_month || this.statMonth;
      this.operationForm = (source.operation_stats || []).map((item) => ({
        metric_type: item.metric_type,
        label: item.label,
        monthly_count:
          item.monthly_count === null || item.monthly_count === undefined
            ? null
            : Number(item.monthly_count),
      }));
      this.notesForm = {
        current_summary: ((source.notes || {}).current_summary || "").trim(),
        next_plan: ((source.notes || {}).next_plan || "").trim(),
      };
      this.technicalItems = ((source.technical || {}).items || []).map(
        (item) => ({ ...item })
      );
      this.completedTechnicalKeys = this.technicalItems
        .filter((item) => item.status === "completed")
        .map((item) => item.item_key);
      this.membersEnabled =
        !Object.prototype.hasOwnProperty.call(source, "members_enabled") ||
        ![false, 0, "0"].includes(source.members_enabled);
      this.memberForm = (source.members || []).map((item) => ({
        role: item.role || "",
        name: item.name || "",
        phone: item.phone || "",
        email: item.email || "",
        contact_image_path: item.contact_image_path || "",
      }));
      this.$nextTick(() => {
        this.savedFingerprint = this.formFingerprint;
      });
    },
    clearEditor() {
      this.reportState = {};
      this.editor = null;
      this.operationForm = [];
      this.notesForm = { current_summary: "", next_plan: "" };
      this.technicalItems = [];
      this.completedTechnicalKeys = [];
      this.membersEnabled = true;
      this.memberForm = [];
      this.savedFingerprint = "";
    },
    confirmDiscard() {
      if (!this.hasUnsavedChanges) return Promise.resolve();
      return this.$confirm("当前月报有未保存修改，确定放弃吗？", "未保存修改", {
        type: "warning",
      });
    },
    onSiteChange(value) {
      const previous = Number(((this.editor || {}).meta || {}).site_id || 0);
      this.confirmDiscard()
        .then(() => {
          this.siteId = value || "";
          this.statMonth = "";
          this.clearEditor();
          this.syncRoute();
          return this.loadOptions();
        })
        .catch(() => {
          this.siteId = previous || "";
        });
    },
    onMonthChange(value) {
      const previous = ((this.editor || {}).meta || {}).stat_month || "";
      this.confirmDiscard()
        .then(() => {
          this.statMonth = value || "";
          if (!this.statMonth) {
            this.clearEditor();
            this.syncRoute();
            return null;
          }
          return this.loadEditor();
        })
        .catch(() => {
          this.statMonth = previous;
        });
    },
    saveDraft() {
      const members = this.memberForm.filter((item) =>
        [
          item.role,
          item.name,
          item.phone,
          item.email,
          item.contact_image_path,
        ].some((value) => String(value || "").trim())
      );
      const invalidMember = members.find(
        (item) =>
          !String(item.role || "").trim() || !String(item.name || "").trim()
      );
      if (invalidMember) {
        this.$message.error("项目成员角色和姓名不能为空");
        return;
      }
      this.saving = true;
      apiSeoEffectReportSave({
        site_id: this.siteId,
        stat_month: this.statMonth,
        revision: this.reportState.revision,
        operation_stats: this.operationForm.map((item) => ({
          metric_type: item.metric_type,
          monthly_count: item.monthly_count,
        })),
        current_summary: this.notesForm.current_summary,
        next_plan: this.notesForm.next_plan,
        technical_items: this.technicalItems.map((item) => ({
          item_key: item.item_key,
          status: this.completedTechnicalKeys.includes(item.item_key)
            ? "completed"
            : "pending",
        })),
        members_enabled: this.membersEnabled,
        members,
      })
        .then((response) => {
          this.applySaveResponse(response);
          this.$message.success("月报草稿已保存");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    addMember() {
      this.memberForm.push({
        role: "",
        name: "",
        phone: "",
        email: "",
        contact_image_path: "",
      });
    },
    removeMember(index) {
      this.memberForm.splice(index, 1);
    },
    toggleTechnicalAll(checked) {
      this.completedTechnicalKeys = checked
        ? this.technicalItems.map((item) => item.item_key)
        : [];
    },
    onMemberImageUpload(index, file) {
      const path = String((file || {}).url || "").replace(/^\/+/, "");
      if (this.memberForm[index] && path) {
        this.$set(this.memberForm[index], "contact_image_path", `/${path}`);
      }
    },
    returnToReport() {
      const query = {};
      if (this.siteId) query.site_id = String(this.siteId);
      if (this.statMonth) query.stat_month = this.statMonth;
      this.$router.push({
        path: "/seo/effect-report",
        query,
      });
    },
    syncRoute() {
      const query = {};
      if (this.siteId) query.site_id = String(this.siteId);
      if (this.statMonth) query.stat_month = this.statMonth;
      const current = this.$route.query || {};
      if (
        String(current.site_id || "") === String(query.site_id || "") &&
        String(current.stat_month || "") === String(query.stat_month || "")
      ) {
        return;
      }
      this.$router.replace({ path: this.$route.path, query }).catch(() => {});
    },
    monthStatusText(status) {
      return {
        published: "已发布",
        draft: "草稿",
        uncreated: "未创建",
      }[status];
    },
    onBeforeUnload(event) {
      if (!this.hasUnsavedChanges) return undefined;
      event.preventDefault();
      event.returnValue = "";
      return "";
    },
  },
};
</script>

<style lang="scss" scoped>
.effect-report-editor-page {
  width: 100%;
  max-width: 1480px;
  min-height: calc(100vh - 72px);
  margin: 0 auto;
  padding: 16px;
  box-sizing: border-box;
  color: #18263d;
  letter-spacing: 0;
}

.editor-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 14px;

  &__identity,
  &__filters {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__identity {
    min-width: 0;

    h1 {
      margin: 0;
      font-size: 22px;
      line-height: 30px;
    }

    p {
      margin: 3px 0 0;
      overflow: hidden;
      color: #758399;
      font-size: 13px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__filters {
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  &__site {
    width: 250px;
  }

  &__month {
    width: 150px;
  }
}

.month-option__status {
  float: right;
  margin-left: 18px;
  color: #a2adbc;
  font-size: 11px;
}

.editor-selection-empty {
  display: flex;
  min-height: 520px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  border: 1px solid #e4eaf2;
  border-radius: 8px;
  background: #fff;
  color: #8a97aa;

  i {
    font-size: 40px;
  }

  strong {
    font-size: 14px;
    font-weight: 500;
  }
}

.editor-surface {
  overflow: hidden;
  border: 1px solid #e4eaf2;
  border-radius: 8px;
  background: #fff;
}

.form-section {
  border-bottom: 1px solid #edf1f7;
  padding: 22px 24px;

  &__head {
    display: flex;
    min-height: 36px;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;

    h2 {
      margin: 0;
      font-size: 16px;
      line-height: 22px;
    }

    p {
      margin: 4px 0 0;
      color: #8a97aa;
      font-size: 12px;
      line-height: 18px;
    }
  }
}

.operation-editor {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  ::v-deep .el-input-number {
    width: 100%;
  }
}

.notes-editor {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;

  .el-form-item {
    margin-bottom: 0;
  }

  ::v-deep textarea {
    line-height: 22px;
    resize: vertical;
  }
}

.technical-select-all {
  flex: 0 0 auto;
  margin-top: 2px;
}

.technical-editor {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px 14px;

  .el-checkbox {
    min-width: 0;
    margin-right: 0;
  }

  ::v-deep .el-checkbox__label {
    max-width: calc(100% - 24px);
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
    white-space: nowrap;
  }
}

.member-editor {
  overflow-x: auto;
  padding-bottom: 2px;

  &__actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 10px;
    color: #758399;
    font-size: 12px;
  }

  &__columns,
  &__row {
    display: grid;
    grid-template-columns:
      minmax(120px, 0.75fr) minmax(120px, 0.75fr)
      minmax(150px, 0.9fr) minmax(200px, 1.2fr) 150px 40px;
    min-width: 980px;
    align-items: center;
    gap: 8px;
  }

  &__columns {
    border-bottom: 1px solid #e8eef6;
    padding: 0 0 8px;
    color: #8a97aa;
    font-size: 12px;
  }

  &__row {
    border-top: 1px solid #f0f3f8;
    padding: 10px 0;
  }

  &__image-field {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 5px;
  }

  &__image-preview {
    display: flex;
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    color: #a2adbc;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__delete {
    color: #e64a55;
  }

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 4px;
    background: #f0f3f8;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background: #cfd8e5;
  }
}

.editor-empty {
  border: 1px dashed #dfe6ef;
  border-radius: 4px;
  padding: 28px;
  color: #a2adbc;
  text-align: center;
}

.editor-actions {
  position: sticky;
  z-index: 3;
  bottom: 0;
  display: flex;
  min-height: 60px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: rgba(255, 255, 255, 0.96);
  padding: 10px 24px;
  color: #8a97aa;
  font-size: 12px;
  backdrop-filter: blur(8px);

  &__dirty {
    color: #d97904;
    font-weight: 600;
  }
}

@media (max-width: 1120px) {
  .editor-toolbar {
    align-items: flex-start;
    flex-direction: column;

    &__filters {
      width: 100%;
      justify-content: flex-start;
    }
  }

  .technical-editor {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .effect-report-editor-page {
    position: fixed;
    z-index: 20;
    top: 56px;
    right: 0;
    bottom: 0;
    left: 0;
    max-width: none;
    min-height: 0;
    overflow-y: auto;
    background: #f5f7fa;
    padding: 12px;
    overscroll-behavior: contain;
  }

  .editor-toolbar {
    &__identity {
      width: 100%;
      align-items: flex-start;
      flex-direction: column;
    }

    &__filters {
      align-items: stretch;
      flex-direction: column;
    }

    &__site,
    &__month {
      width: 100%;
    }
  }

  .form-section {
    padding: 18px 16px;
  }

  .operation-editor,
  .notes-editor,
  .technical-editor {
    grid-template-columns: minmax(0, 1fr);
  }

  .form-section__head {
    flex-wrap: wrap;
  }

  .member-editor__actions {
    width: 100%;
    justify-content: flex-end;
  }

  .editor-actions {
    padding: 10px 16px;
  }
}
</style>
